import { IBrowserAccessor } from './browser';
import { ILogger } from './logger';
import { expect } from './expect';
import {
  GCLID,
  BFGCLID,
  GoogleAdsHandlerSettings,
  GoogleAdsPreset,
  GoogleAds, STORAGE_KEY_GOOGLE_ADS_PARAMETERS,
} from './google-ads.types';

export class GoogleAdsHandler {
  private readonly browser: IBrowserAccessor;
  private readonly logger: ILogger;
  private readonly bfIsEnabled: boolean;
  private readonly bfLifetimeInDays: number;

  private gclid: string;
  private bfgclid: string;

  /*
   * Gets the parsed Google Click ID value.
   * If Borderfree logic is enabled and `bfgclid` parameter has
   * value then it returns value of `gclid` provided by Borderfree.
   */
  public get googleClickId(): string {
    return this.isBorderfreeGoogleClickId ? this.bfgclid : this.gclid;
  }

  /*
   * Gets true when Borderfree logic is enabled and Borderfree gclid parameter found.
   */
  public get isBorderfreeGoogleClickId(): string {
    return this.bfIsEnabled && this.bfgclid;
  }

  /**
   * Initializes a new instance of GoogleAdsHandler class.
   */
  constructor(browser: IBrowserAccessor, logger: ILogger, settings?: GoogleAdsHandlerSettings) {
    this.browser = expect(browser)
      .toBeDefined('The IBrowserAccessor implementation is required to construct GoogleAdsHandler.');
    this.logger = expect(logger)
      .toBeDefined('The ILogger implementation is required to construct GoogleAdsHandler.');

    this.bfIsEnabled = settings?.bfGoogleAdsEnabled ?? true;
    this.bfLifetimeInDays = settings?.bfGoogleAdsLifetimeInDays ?? 30;
  }

  /**
   * Parses Google Click ID (gclid) parameter from the URL.
   */
  public async handle(customInput?: GoogleAdsPreset): Promise<void> {
    if (!this.bfIsEnabled) {
      return;
    }

    const input = this.parseUrl(customInput);
    const storageStrategy = this.selectStorageStrategy();
    const output = await storageStrategy.execute(input);
    this.gclid = output.gclid;
    this.bfgclid = output.bfgclid;
  }

  private parseUrl(input?: string | GoogleAdsPreset): GoogleAds {
    const result = { gclid: null, bfgclid: null };
    try {
      if (isUrl(input)) { // if URL with gclid provided outside, then parse it assuming it contains gclid
        return getFromPresetUrl(input)
      }
      if (isPresetObject(input)) { // if object with gclid provided then use it as output
        return getFromPresetObject(input);
      }

      // default flow is to parse URL or get defaults based on referrer
      const url = new URL(this.browser.getPageUrl());
      result.gclid = url.searchParams.get(GCLID);
      result.bfgclid = url.searchParams.get(BFGCLID);
    } catch (error) {
      this.logger.writeErrorLog({
        method: 'GoogleAdsHandler::parseUrl()',
        message: error.message,
        errorMessage: error.message,
        errorStackTrace: error.stackTrace,
      });
    }

    return result;

    function isUrl(_input: unknown): _input is string {
      return typeof _input === "string";
    }
    function getFromPresetUrl(_input: string): GoogleAds {
      const url = new URLSearchParams(_input);
      return {
        gclid: url.get(GCLID),
        bfgclid: url.get(BFGCLID)
      };
    }
    function isPresetObject(_input: unknown): _input is GoogleAds {
      return (
        typeof _input === "object" &&
        _input !== null &&
        "gclid" in _input &&
        "bfgclid" in _input &&
        typeof (_input as GoogleAds).gclid === "string" &&
        typeof (_input as GoogleAds).bfgclid === "string"
      );
    }
    function getFromPresetObject(_input: GoogleAds): GoogleAds {
      return _input;
    }
  }

  private selectStorageStrategy() {
    // check if borderfree support is enbaled
    // if enabled then return BorderfreeStorageStrategy
    // otherwise DefaultStorageStrategy

    if (this.bfIsEnabled) {
      return new BorderfreeStorageStrategy(this.browser, this.bfLifetimeInDays);
    }

    return new DefaultStorageStrategy(this.browser);
  }
}

interface IStorageStrategy {
  execute(googleAdsParameters: GoogleAds): Promise<GoogleAds>;
}

class DefaultStorageStrategy implements IStorageStrategy {
  private readonly browser: IBrowserAccessor;

  constructor(browser: IBrowserAccessor) {
    this.browser = expect(browser)
      .toBeDefined('The IBrowserAccessor implementation is required to construct DefaultStorageStrategy.');
  }

  public async execute(parameters: GoogleAds): Promise<GoogleAds> {
    try {
      if (parameters.gclid) {
        const outsideParameters = {
          gclid: parameters.gclid,
          expiry: Date.now() + 30 * 24 * 60 * 60 * 1000 // 30 days
        };
        const outsideParametersJson = JSON.stringify(outsideParameters);
        await this.browser.localStorage.setItem(STORAGE_KEY_GOOGLE_ADS_PARAMETERS, outsideParametersJson);
        return { gclid: outsideParameters.gclid, bfgclid: null };
      } else {
        const existingParametersJson = await this.browser.localStorage.getItem(STORAGE_KEY_GOOGLE_ADS_PARAMETERS);
        const existingParameters = JSON.parse(existingParametersJson);

        if (existingParameters?.expiry && existingParameters.expiry < Date.now()) {
          await this.browser.localStorage.removeItem(STORAGE_KEY_GOOGLE_ADS_PARAMETERS);
          return { gclid: null, bfgclid: null };
        } else {
          return { gclid: existingParameters?.gclid, bfgclid: null };
        }
      }
    } catch {
      return { gclid: null, bfgclid: null };
    }
  }
}

class BorderfreeStorageStrategy implements IStorageStrategy {
  private readonly browser: IBrowserAccessor;
  private readonly bfLifetimeInDays: number;
  constructor(browser: IBrowserAccessor, bfLifetimeInDays: number) {
    this.browser = expect(browser)
      .toBeDefined('The IBrowserAccessor implementation is required to construct BorderfreeStorageStrategy.');
    this.bfLifetimeInDays = bfLifetimeInDays;
  }

  public async execute(parameters: GoogleAds): Promise<GoogleAds> {
    try {
      if (parameters.bfgclid) {
        const outsideParameters = {
          bfgclid: parameters.bfgclid,
          expiry: Date.now() + this.bfLifetimeInDays * 24 * 60 * 60 * 1000 // 30 days
        };
        const outsideParametersJson = JSON.stringify(outsideParameters);
        await this.browser.localStorage.setItem(STORAGE_KEY_GOOGLE_ADS_PARAMETERS, outsideParametersJson);
        return { gclid: null, bfgclid: parameters.bfgclid };
      } else {
        const existingParametersJson = await this.browser.localStorage.getItem(STORAGE_KEY_GOOGLE_ADS_PARAMETERS);
        const existingParameters = JSON.parse(existingParametersJson);
        if (existingParameters?.expiry && existingParameters.expiry < Date.now()) {
          await this.browser.localStorage.removeItem(STORAGE_KEY_GOOGLE_ADS_PARAMETERS);
        } else if (existingParameters?.bfgclid) {
          return { gclid: null, bfgclid: existingParameters.bfgclid };
        } else if (existingParameters?.gclid) {
          return { gclid: existingParameters.gclid, bfgclid: null };
        }

        if (parameters.gclid) {
          const outsideParameters = {
            gclid: parameters.gclid,
            expiry: Date.now() + 30 * 24 * 60 * 60 * 1000 // 30 days
          };
          const outsideParametersJson = JSON.stringify(outsideParameters);
          await this.browser.localStorage.setItem(STORAGE_KEY_GOOGLE_ADS_PARAMETERS, outsideParametersJson);
          return { gclid: outsideParameters.gclid, bfgclid: null };
        } else {
          return { gclid: null, bfgclid: null };
        }
      }
    } catch {
      return { gclid: null, bfgclid: null };
    }
  }
}
